export interface MarkerDeclutterPoint<T> {
    id: T;
    x: number;
    y: number;
    alwaysVisible?: boolean;
}

const getCircleOverlapFraction = (distanceSquared: number, radius: number): number => {
    if (distanceSquared <= 0) return 1;

    const diameter = radius * 2;
    if (distanceSquared >= diameter * diameter) return 0;

    const distance = Math.sqrt(distanceSquared);
    const overlapArea = (2 * radius * radius * Math.acos(distance / diameter))
        - (distance / 2 * Math.sqrt((diameter * diameter) - distanceSquared));

    return overlapArea / (Math.PI * radius * radius);
};

/**
 * Selects markers using an additive estimate of how much of each candidate is
 * already covered. The spatial grid limits comparisons to accepted markers
 * close enough to overlap the candidate.
 */
export function selectDeclutteredMarkerIds<T>(
    points: MarkerDeclutterPoint<T>[],
    radius: number,
    rejectionCoverage: number
): Set<T> {
    if (points.length === 0) return new Set<T>();
    if (radius <= 0 || rejectionCoverage <= 0 || rejectionCoverage > 1) {
        throw new RangeError('Marker radius must be positive and rejection coverage must be greater than 0 and at most 1.');
    }

    const diameter = radius * 2;
    const diameterSquared = diameter * diameter;
    const grid = new Map<string, MarkerDeclutterPoint<T>[]>();
    const selected = new Set<T>();

    // Important markers are accepted first so they cannot be hidden by an
    // ordinary marker. Original order remains the tie-breaker in each group.
    const priorityPoints: MarkerDeclutterPoint<T>[] = [];
    const ordinaryPoints: MarkerDeclutterPoint<T>[] = [];
    points.forEach(point => {
        (point.alwaysVisible ? priorityPoints : ordinaryPoints).push(point);
    });
    const orderedPoints = priorityPoints.concat(ordinaryPoints);

    const getCellCoordinate = (value: number) => Math.floor(value / diameter);
    const getCellKey = (x: number, y: number) => `${x}:${y}`;

    for (const point of orderedPoints) {
        const cellX = getCellCoordinate(point.x);
        const cellY = getCellCoordinate(point.y);
        let coveredFraction = 0;

        if (!point.alwaysVisible) {
            for (let y = cellY - 1; y <= cellY + 1 && coveredFraction < rejectionCoverage; y++) {
                for (let x = cellX - 1; x <= cellX + 1 && coveredFraction < rejectionCoverage; x++) {
                    const nearbyPoints = grid.get(getCellKey(x, y));
                    if (!nearbyPoints) continue;

                    for (const nearbyPoint of nearbyPoints) {
                        const deltaX = point.x - nearbyPoint.x;
                        const deltaY = point.y - nearbyPoint.y;
                        const distanceSquared = (deltaX * deltaX) + (deltaY * deltaY);
                        if (distanceSquared >= diameterSquared) continue;

                        coveredFraction += getCircleOverlapFraction(distanceSquared, radius);
                        if (coveredFraction >= rejectionCoverage) break;
                    }
                }
            }
        }

        if (point.alwaysVisible || coveredFraction < rejectionCoverage) {
            selected.add(point.id);
            const cellKey = getCellKey(cellX, cellY);
            const cellPoints = grid.get(cellKey);
            if (cellPoints) {
                cellPoints.push(point);
            }
            else {
                grid.set(cellKey, [point]);
            }
        }
    }

    return selected;
}
